<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--info-mandat"></div>
      <div class="modal__back" @click="goBack()"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form form--terminate">
        <div class="navigators">

          <div class="navigator">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">
              {{ $t(`addMandateModal.${"infoTitle"}`) }}
            </div>
          </div>
        </div>
        <div class="add__form">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <div class="termination">


                <div class="termination__row termination__row--digital">
                  <div class="termination__row-inner">
                    <div class="form__field">
                      <div class="input input--default input--readonly">
                        {{ parseCustomerName(customerDetails) }}
                      </div>
                    </div>
                  </div>
                  <div class="termination__row-inner">
                    <div class="form__field">
                      <VSelect class="select select--default" :options="availableAgents" :reduce="(agent) => agent.id"
                        :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`"
                        :class="{ 'input--error': agentHasError }" v-model="agentId"
                        :placeholder="$t(`addMandateModal.${'agent'}`)" />
                      <div class="form__error" v-if="agentHasError">
                        {{ $t(`addMandateModal.${"agentErrorMsg"}`) }}
                      </div>
                    </div>
                    <div class="form__field">
                      <VSelect class="select select--default" :options="availableNoticeAgents"
                        :reduce="(agent) => agent.id"
                        :getOptionLabel="(agent) => agent && agent.id === null ? $t(`addMandateModal.${'noticeAgent'}`) : `${agent.first_name} ${agent.last_name}`"
                        v-model="noticeAgentId" :placeholder="$t(`addMandateModal.${'noticeAgent'}`)" />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading"></div>
      <div v-else class="modal-buttons">
        <div class="button-group">
          <button class="modal__button modal__button--submit button button--default" @click="addMandate()">
            <span>{{ $t(`addMandateModal.${'saveBtn'}`) }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Loading from "@/partials/Loading";
import { parseCustomerName } from "@/helpers/entityParsers";

defineOptions({
  name: "AddMandateModal"
});

const { t } = useI18n();

const store = useStore();

// Reactive properties
const formLoading = ref(true);
const isRequesting = ref(false);

const customerId = ref("");
const mandateTypeId = ref(13);

const agentId = ref(null);
const noticeAgentId = ref(null);

const agentHasError = ref(false);
// Computed properties (Mapping Vuex getters and store mappings)
const customerDetails = computed(() => store.getters.customerDetails);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const teamAgents = ref([]);
const canAccessMandates = computed(() => {
  if (isSuperAdmin.value) return true;

  const teamId = Number(
    customerDetails.value?.team_id
    ?? customerDetails.value?.team?.id
    ?? customerDetails.value?.teamId
    ?? 0
  );

  if (!teamId) return false;

  return store.getters.hasTeamPermission("mandate_access", teamId);
});

const agents = computed(() => {
  if (Array.isArray(teamAgents.value)) return teamAgents.value;
  if (Array.isArray(teamAgents.value?.agents)) return teamAgents.value.agents;
  return [];
});

const normalizeAgentValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return Number(value);
};

const availableAgents = computed(() => {
  const excludedId = normalizeAgentValue(noticeAgentId.value);

  return agents.value.filter((agent) => {
    return excludedId === null || normalizeAgentValue(agent.id) !== excludedId;
  });
});

const availableNoticeAgents = computed(() => {
  const excludedId = normalizeAgentValue(agentId.value);

  const filteredAgents = agents.value.filter((agent) => {
    return excludedId === null || normalizeAgentValue(agent.id) !== excludedId;
  });

  return [
    {
      id: null,
      first_name: "",
      last_name: "",
      label: t(`addMandateModal.${'noticeAgent'}`),
    },
    ...filteredAgents,
  ];
});

// Watchers
watch(agentId, (value) => {
  const selectedValue = normalizeAgentValue(value);
  const noticeValue = normalizeAgentValue(noticeAgentId.value);

  if (selectedValue !== null && noticeValue !== null && selectedValue === noticeValue) {
    noticeAgentId.value = null;
  }

  if (value) {
    agentHasError.value = false;
  }
});

watch(noticeAgentId, (value) => {
  const selectedValue = normalizeAgentValue(value);
  const agentValue = normalizeAgentValue(agentId.value);

  if (selectedValue !== null && agentValue !== null && selectedValue === agentValue) {
    agentId.value = null;
  }
});

// Operational Methods
const closeModal = () => {
  store.commit("setShowAddInfoMandateModal", false);
};

const goBack = () => {
  closeModal();
  store.commit("setShowAddActivityModal", true);
};


const validateFields = (callback) => {
  agentHasError.value = !agentId.value;

  callback(!agentHasError.value);
};

const addMandate = async () => {
  if (!canAccessMandates.value) {
    store.commit("setToast", {
      display: true,
      type: "error",
      message: t("messages.noPermission"),
    });
    return;
  }

  isRequesting.value = true;

  validateFields(async (isValid) => {
    if (!isValid) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: t(`addMandateModal.validationErrorMsg`),
      });
      isRequesting.value = false;
      return;
    }

    try {
      const mandatePayload = {
        customer_id: customerId.value,
        mandate_type_id: mandateTypeId.value,
        agent_id: agentId.value,
        notice_agent_id: noticeAgentId.value,
        deliveries: [],
      };

      await store.dispatch("createMandate", mandatePayload);

      closeModal();
    } catch (error) {
      console.error("Action failed:", error);
    } finally {
      isRequesting.value = false;
    }
  });
};
const initializeComponent = async () => {
  formLoading.value = true;

  if (!canAccessMandates.value) {
    formLoading.value = false;
    closeModal();
    return;
  }

  if (customerDetails.value?.team) {
    const teamId = Number(customerDetails.value.team.id);
    const response = await store.dispatch("getTeamAgents", {
      teamId,
      model: "mandate_create",
    });

    teamAgents.value = Array.isArray(response?.agents)
      ? response.agents
      : Array.isArray(response)
        ? response
        : [];
  }

  customerId.value = customerDetails.value?.id || "";
  formLoading.value = false;
};

initializeComponent();

onUnmounted(() => {
  closeModal();
});
</script>

<style scoped lang="scss">
@use "../assets/scss/add-panel-modal";
@use "../assets/scss/termination-potential-policy";

.modal__panel {
  height: 420px;
}

.termination {
  padding: 0;
}

.termination__row {
  &--digital {
    width: 70% !important;
    display: flex;
    flex-direction: column;
    margin-left: auto !important;
    margin-right: auto !important;
    margin-bottom: 0 !important;
    border: none !important;
  }

}

.termination__row-inner {
  display: flex;
  gap: 20px;
  width: 100%;

  .form__field {
    flex: 1;
  }
}

.add__form,
.customer__form .form--terminate {
  width: 100% !important;
}

@media screen and (max-width: 500px) {
  .add__content { 
    min-height: 200px;
  }
}
</style>
